/**
 * @module NotificationsFsl
 * @package @geenius/notifications/shared
 * @description Centralizes the shared FSL inputs for the notifications package
 * so every runtime variant consumes the same translation keys, localized date
 * labels, and SEO metadata builder.
 */

import { t as translate } from '@geenius/i18n/shared'
import {
  buildCanonical,
  createPageMeta,
  type SEOMeta,
  webApplicationSchema,
} from '@geenius/seo/shared'
import en from './locales/en.json'
import type {
  Notification,
  NotificationChannel,
  NotificationStatus,
  NotificationType,
} from './types'

type NotificationParams = Record<string, string | number>
type NotificationTranslator = (key: string, params?: NotificationParams) => string

export const NOTIFICATION_DEFAULT_TRANSLATIONS = en

export const NOTIFICATION_CHANNEL_TRANSLATION_KEYS: Readonly<
  Record<NotificationChannel, string>
> = {
  'in-app': 'notifications.channels.inApp',
  email: 'notifications.channels.email',
  push: 'notifications.channels.push',
  sms: 'notifications.channels.sms',
}

export const NOTIFICATION_TYPE_TRANSLATION_KEYS: Readonly<
  Record<NotificationType, string>
> = {
  info: 'notifications.types.info',
  success: 'notifications.types.success',
  warning: 'notifications.types.warning',
  error: 'notifications.types.error',
  mention: 'notifications.types.mention',
  invite: 'notifications.types.invite',
  payment: 'notifications.types.payment',
  system: 'notifications.types.system',
}

export const NOTIFICATION_STATUS_TRANSLATION_KEYS: Readonly<
  Partial<Record<NotificationStatus | 'all', string>>
> = {
  all: 'notifications.filters.status.all',
  unread: 'notifications.filters.status.unread',
  read: 'notifications.filters.status.read',
}

const NOTIFICATIONS_SITE_URL = 'https://geenius.dev'
const NOTIFICATIONS_SITE_NAME = 'Geenius Notifications'
const NOTIFICATIONS_OG_IMAGE = 'https://geenius.dev/og/notifications.png'

function buildPluralKey(key: string, count: number): string {
  if (count === 0) {
    return `${key}_zero`
  }

  if (count === 1) {
    return `${key}_one`
  }

  return `${key}_other`
}

function humanizeToken(value: string): string {
  return value
    .replaceAll(/[-_]+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

function translateWithFallback(
  translator: NotificationTranslator,
  key: string,
  params?: NotificationParams,
): string {
  const translated = translator(key, params)

  if (translated !== key) {
    return translated
  }

  return translateNotifications(key, params)
}

export function translateNotifications(
  key: string,
  params?: NotificationParams,
): string {
  return translate(key, NOTIFICATION_DEFAULT_TRANSLATIONS, params)
}

export function pluralizeNotifications(
  key: string,
  count: number,
  params?: NotificationParams,
): string {
  const interpolationParams = { count, ...params }
  const pluralKey = buildPluralKey(key, count)
  const pluralValue = translate(pluralKey, NOTIFICATION_DEFAULT_TRANSLATIONS, interpolationParams)

  if (pluralValue !== pluralKey) {
    return pluralValue
  }

  return translate(key, NOTIFICATION_DEFAULT_TRANSLATIONS, interpolationParams)
}

export function getNotificationChannelLabel(
  channel: string,
  translator: NotificationTranslator = translateNotifications,
): string {
  const key = NOTIFICATION_CHANNEL_TRANSLATION_KEYS[channel as NotificationChannel]
  return key ? translateWithFallback(translator, key) : humanizeToken(channel)
}

export function getNotificationTypeLabel(
  type: string,
  translator: NotificationTranslator = translateNotifications,
): string {
  const key = NOTIFICATION_TYPE_TRANSLATION_KEYS[type as NotificationType]
  return key ? translateWithFallback(translator, key) : humanizeToken(type)
}

export function getNotificationStatusLabel(
  status: NotificationStatus | 'all',
  translator: NotificationTranslator = translateNotifications,
): string {
  const key = NOTIFICATION_STATUS_TRANSLATION_KEYS[status]
  return key ? translateWithFallback(translator, key) : humanizeToken(status)
}

export function formatNotificationDateLabel(
  createdAt: string | Date,
  translator: NotificationTranslator = translateNotifications,
  locale?: string,
): string {
  const notificationDate = createdAt instanceof Date ? createdAt : new Date(createdAt)
  const now = new Date()
  const today = now.toDateString()
  const yesterday = new Date(now.getTime() - 86_400_000).toDateString()

  if (notificationDate.toDateString() === today) {
    return translateWithFallback(translator, 'notifications.dateBuckets.today')
  }

  if (notificationDate.toDateString() === yesterday) {
    return translateWithFallback(translator, 'notifications.dateBuckets.yesterday')
  }

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(notificationDate)
}

export function formatNotificationRelativeTime(
  createdAt: string,
  translator: NotificationTranslator = translateNotifications,
  locale?: string,
): string {
  const timestamp = new Date(createdAt)
  const diffMs = Date.now() - timestamp.getTime()
  const minutes = Math.floor(diffMs / 60_000)

  if (minutes < 1) {
    return translateWithFallback(translator, 'notifications.time.justNow')
  }

  if (minutes < 60) {
    return translateWithFallback(
      translator,
      buildPluralKey('notifications.time.minutes', minutes),
      { count: minutes },
    )
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return translateWithFallback(
      translator,
      buildPluralKey('notifications.time.hours', hours),
      { count: hours },
    )
  }

  const days = Math.floor(hours / 24)
  if (days < 7) {
    return translateWithFallback(
      translator,
      buildPluralKey('notifications.time.days', days),
      { count: days },
    )
  }

  if (days < 30) {
    const weeks = Math.floor(days / 7)
    return translateWithFallback(
      translator,
      buildPluralKey('notifications.time.weeks', weeks),
      { count: weeks },
    )
  }

  return formatNotificationDateLabel(timestamp, translator, locale)
}

export function groupNotificationsForDisplay(
  notifications: Notification[],
  translator: NotificationTranslator = translateNotifications,
  locale?: string,
): Map<string, Notification[]> {
  const groups = new Map<string, Notification[]>()

  for (const notification of notifications) {
    const label = formatNotificationDateLabel(notification.createdAt, translator, locale)
    const bucket = groups.get(label)

    if (bucket) {
      bucket.push(notification)
    } else {
      groups.set(label, [notification])
    }
  }

  return groups
}

export interface BuildNotificationsSeoMetaOptions {
  title: string
  description: string
  path: string
  keywords: string[]
}

export function buildNotificationsSeoMeta(
  options: BuildNotificationsSeoMetaOptions,
): SEOMeta {
  return createPageMeta(
    {
      siteName: NOTIFICATIONS_SITE_NAME,
      siteUrl: NOTIFICATIONS_SITE_URL,
      titleTemplate: '%s | Geenius Notifications',
      defaultImage: NOTIFICATIONS_OG_IMAGE,
      twitterHandle: '@geeniusdev',
    },
    options.title,
    options.description,
  )
    .withCanonical(buildCanonical(options.path, NOTIFICATIONS_SITE_URL))
    .withKeywords(options.keywords)
    .withImage(
      NOTIFICATIONS_OG_IMAGE,
      translateNotifications('notifications.seo.imageAlt'),
    )
    .withType('website')
    .withJsonLd(
      webApplicationSchema({
        name: NOTIFICATIONS_SITE_NAME,
        url: buildCanonical(options.path, NOTIFICATIONS_SITE_URL),
        description: options.description,
        applicationCategory: 'BusinessApplication',
      }),
    )
    .build()
}
